import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  BellRing,
  BriefcaseBusiness,
  CalendarClock,
  Check,
  Clock3,
  Files,
  Mail,
  MessageCircleMore,
  LoaderCircle,
  Plus,
  Plug,
  Search,
  Scale,
  Smartphone,
  Trash2,
  Users,
} from "lucide-react";
import {
  ALL_COURTS_ID,
  CHANNEL_LABELS,
  EVENT_LABELS,
  EVENT_TYPES,
  FILTER_MODES,
  INTEGRATION_TYPES,
  SAVED_EXPEDIENT_MODES,
  formatCuij,
  normalizeCuij,
} from "@tribunalea/shared";
import { useApi } from "../App.jsx";
import { useRouter } from "@tribunalea/ui";

const emptyRule = {
  name: "",
  filter: {
    mode: FILTER_MODES.CUIJ,
    cuij: "",
    jurisdictions: [],
    savedExpedientes: {
      mode: SAVED_EXPEDIENT_MODES.ALL,
      cuijs: [],
    },
    followUps: {
      mode: SAVED_EXPEDIENT_MODES.ALL,
      cuijs: [],
    },
    integration: { type: "" },
  },
  event: { type: EVENT_TYPES.ANY_MOVEMENT, stuckBusinessDays: 10 },
  channels: ["email"],
  recipients: { mode: "all", userIds: [] },
};

function normalizeRule(existing) {
  return {
    ...emptyRule,
    ...existing,
    filter: {
      ...emptyRule.filter,
      ...existing.filter,
      savedExpedientes: {
        ...emptyRule.filter.savedExpedientes,
        ...existing.filter?.savedExpedientes,
      },
      followUps: {
        ...emptyRule.filter.followUps,
        ...existing.filter?.followUps,
      },
    },
    event: {
      ...emptyRule.event,
      ...existing.event,
      type:
        existing.event?.type === "attention_required"
          ? EVENT_TYPES.STAGE_CHANGE
          : existing.event?.type || emptyRule.event.type,
    },
  };
}

const eventOptions = [
  {
    value: EVENT_TYPES.ANY_MOVEMENT,
    title: EVENT_LABELS[EVENT_TYPES.ANY_MOVEMENT],
    description: "Cada nueva actuación detectada en los expedientes.",
    icon: BellRing,
  },
  {
    value: EVENT_TYPES.STAGE_CHANGE,
    title: EVENT_LABELS[EVENT_TYPES.STAGE_CHANGE],
    description:
      "Cuando Praxis detecte que el expediente cambió de etapa procesal.",
    icon: Scale,
  },
  {
    value: EVENT_TYPES.STUCK,
    title: EVENT_LABELS[EVENT_TYPES.STUCK],
    description: "Al alcanzar una cantidad de días hábiles sin movimientos.",
    icon: Clock3,
  },
];

const integrationEventOption = {
  value: EVENT_TYPES.NEW_INTEGRATION_NOTIFICATION,
  title: EVENT_LABELS[EVENT_TYPES.NEW_INTEGRATION_NOTIFICATION],
  description:
    "Cuando el Sistema de Notificaciones del PJM publique una nueva notificación.",
  icon: BellRing,
};

const channelOptions = [
  { value: "email", label: CHANNEL_LABELS.email, icon: Mail },
  { value: "sms", label: CHANNEL_LABELS.sms, icon: Smartphone },
  { value: "whatsapp", label: CHANNEL_LABELS.whatsapp, icon: MessageCircleMore },
];

export function RuleFormPage() {
  const api = useApi();
  const { navigate, path } = useRouter();
  const id = path.match(/^\/alertas\/([^/]+)\/editar$/)?.[1];
  const [rule, setRule] = useState(emptyRule);
  const [circunscripciones, setCircunscripciones] = useState([]);
  const [team, setTeam] = useState([]);
  const [savedExpedientes, setSavedExpedientes] = useState([]);
  const [savedExpedientesLoading, setSavedExpedientesLoading] = useState(true);
  const [savedExpedienteSearch, setSavedExpedienteSearch] = useState("");
  const [followUps, setFollowUps] = useState([]);
  const [followUpsLoading, setFollowUpsLoading] = useState(true);
  const [pjmIntegrationConnected, setPjmIntegrationConnected] =
    useState(false);
  const [selection, setSelection] = useState({
    circunscripcion: "",
    type: "",
    court: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(Boolean(id));
  const [optionsLoading, setOptionsLoading] = useState(true);

  useEffect(() => {
    let active = true;

    setOptionsLoading(true);
    api
      .judiciaryCatalog()
      .then((catalog) => {
        if (active) setCircunscripciones(catalog);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setOptionsLoading(false);
      });

    api
      .team()
      .then((members) => {
        if (active) setTeam(members);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      });

    api
      .listIntegrations()
      .then((items) => {
        if (active) {
          setPjmIntegrationConnected(
            items.some(
              (item) =>
                item.type === INTEGRATION_TYPES.PJM_NOTIFICATIONS
                && item.configured,
            ),
          );
        }
      })
      .catch(() => {
        if (active) setPjmIntegrationConnected(false);
      });

    setSavedExpedientesLoading(true);
    api
      .listExpedientes()
      .then((expedientes) => {
        if (active) setSavedExpedientes(expedientes);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setSavedExpedientesLoading(false);
      });

    setFollowUpsLoading(true);
    api
      .listFollowUps()
      .then((items) => {
        if (active) setFollowUps(items);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setFollowUpsLoading(false);
      });

    if (id) {
      api
        .getRule(id)
        .then((existing) => {
          if (active) setRule(normalizeRule(existing));
        })
        .catch((requestError) => {
          if (active) setError(requestError.message);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }

    return () => {
      active = false;
    };
  }, [id]);

  const selectedCircunscripcion = circunscripciones.find(
    (item) => item.id === selection.circunscripcion,
  );
  const types = selectedCircunscripcion?.types || [];
  const selectedType = types.find((item) => item.id === selection.type);
  const courts = selectedType?.courts || [];
  const savedExpedienteChoices = useMemo(() => {
    const byCuij = new Map(
      savedExpedientes.map((expediente) => [
        normalizeCuij(expediente.cuij),
        expediente,
      ]),
    );
    for (const cuij of rule.filter.savedExpedientes.cuijs) {
      const normalized = normalizeCuij(cuij);
      if (!byCuij.has(normalized)) {
        byCuij.set(normalized, {
          cuij,
          caratula: "Expediente archivado o no disponible",
          unavailable: true,
        });
      }
    }
    return [...byCuij.values()];
  }, [savedExpedientes, rule.filter.savedExpedientes.cuijs]);
  const visibleSavedExpedientes = useMemo(() => {
    const term = savedExpedienteSearch.trim().toLocaleLowerCase("es");
    if (!term) return savedExpedienteChoices;
    return savedExpedienteChoices.filter((expediente) =>
      [expediente.cuij, expediente.caratula, expediente.organismoActual]
        .filter(Boolean)
        .some((value) =>
          String(value).toLocaleLowerCase("es").includes(term),
        ),
    );
  }, [savedExpedienteChoices, savedExpedienteSearch]);
  const followUpChoices = useMemo(() => {
    const byCuij = new Map(
      followUps.map((item) => [normalizeCuij(item.cuij), item]),
    );
    for (const cuij of rule.filter.followUps.cuijs) {
      const normalized = normalizeCuij(cuij);
      if (!byCuij.has(normalized)) {
        byCuij.set(normalized, {
          cuij,
          caratula: "Seguimiento no disponible",
          unavailable: true,
        });
      }
    }
    return [...byCuij.values()];
  }, [followUps, rule.filter.followUps.cuijs]);
  const visibleFollowUps = useMemo(() => {
    const term = savedExpedienteSearch.trim().toLocaleLowerCase("es");
    if (!term) return followUpChoices;
    return followUpChoices.filter((item) =>
      [item.cuij, item.caratula, item.organismoActual]
        .filter(Boolean)
        .some((value) =>
          String(value).toLocaleLowerCase("es").includes(term),
        ),
    );
  }, [followUpChoices, savedExpedienteSearch]);
  const usesFollowUps =
    rule.filter.mode === FILTER_MODES.FOLLOW_UPS;
  const activeCollectionKey = usesFollowUps
    ? "followUps"
    : "savedExpedientes";
  const activeCollection = rule.filter[activeCollectionKey];
  const activeExpedienteChoices = usesFollowUps
    ? followUpChoices
    : savedExpedienteChoices;
  const visibleActiveExpedientes = usesFollowUps
    ? visibleFollowUps
    : visibleSavedExpedientes;
  const activeExpedientesLoading = usesFollowUps
    ? followUpsLoading
    : savedExpedientesLoading;
  const activeCollectionLabel = usesFollowUps
    ? "Mis seguimientos"
    : "Mis expedientes";

  const addJurisdiction = () => {
    const circunscripcion = circunscripciones.find(
      (item) => item.id === selection.circunscripcion,
    );
    const type = types.find((item) => item.id === selection.type);
    const court = courts.find((item) => item.id === selection.court);
    if (!circunscripcion || !type || !court) return;
    const duplicate = rule.filter.jurisdictions.some(
      (item) =>
        item.circunscripcionId === circunscripcion.id &&
        item.typeId === type.id &&
        item.courtId === court.id,
    );
    if (duplicate) return;
    setRule((current) => ({
      ...current,
      filter: {
        ...current.filter,
        jurisdictions: [
          ...current.filter.jurisdictions,
          {
            circunscripcionId: circunscripcion.id,
            circunscripcionLabel: titleCase(circunscripcion.label),
            typeId: type.id,
            typeLabel: titleCase(type.label),
            courtId: court.id,
            courtLabel: titleCase(court.label),
          },
        ],
      },
    }));
    setSelection({ circunscripcion: "", type: "", court: "" });
  };

  const removeJurisdiction = (index) => {
    setRule((current) => ({
      ...current,
      filter: {
        ...current.filter,
        jurisdictions: current.filter.jurisdictions.filter(
          (_item, itemIndex) => itemIndex !== index,
        ),
      },
    }));
  };

  const toggleChannel = (channel) => {
    setRule((current) => ({
      ...current,
      channels: current.channels.includes(channel)
        ? current.channels.filter((item) => item !== channel)
        : [...current.channels, channel],
    }));
  };

  const toggleRecipient = (userId) => {
    setRule((current) => ({
      ...current,
      recipients: {
        ...current.recipients,
        userIds: current.recipients.userIds.includes(userId)
          ? current.recipients.userIds.filter((item) => item !== userId)
          : [...current.recipients.userIds, userId],
      },
    }));
  };

  const toggleScopeExpediente = (cuij) => {
    setRule((current) => {
      const collectionKey =
        current.filter.mode === FILTER_MODES.FOLLOW_UPS
          ? "followUps"
          : "savedExpedientes";
      const selected = current.filter[collectionKey].cuijs;
      const normalized = normalizeCuij(cuij);
      const alreadySelected = selected.some(
        (item) => normalizeCuij(item) === normalized,
      );
      return {
        ...current,
        filter: {
          ...current.filter,
          [collectionKey]: {
            ...current.filter[collectionKey],
            cuijs: alreadySelected
              ? selected.filter(
                  (item) => normalizeCuij(item) !== normalized,
                )
              : [...selected, cuij],
          },
        },
      };
    });
  };

  const computedName = useMemo(() => {
    if (rule.name) return rule.name;
    if (rule.filter.mode === FILTER_MODES.CUIJ && rule.filter.cuij) {
      return `Seguimiento ${rule.filter.cuij}`;
    }
    if (rule.filter.mode === FILTER_MODES.SAVED_EXPEDIENTES) {
      return rule.filter.savedExpedientes.mode === SAVED_EXPEDIENT_MODES.ALL
        ? "Todos mis expedientes"
        : `Seguimiento de ${rule.filter.savedExpedientes.cuijs.length} expedientes`;
    }
    if (rule.filter.mode === FILTER_MODES.FOLLOW_UPS) {
      return rule.filter.followUps.mode === SAVED_EXPEDIENT_MODES.ALL
        ? "Todos mis seguimientos"
        : `Alerta de ${rule.filter.followUps.cuijs.length} seguimientos`;
    }
    if (rule.filter.mode === FILTER_MODES.INTEGRATION) {
      return "Nuevas notificaciones del PJM";
    }
    const first = rule.filter.jurisdictions[0];
    return first ? `${first.typeLabel} · ${first.circunscripcionLabel}` : "";
  }, [rule.name, rule.filter]);

  const scopeSummary = useMemo(() => {
    if (rule.filter.mode === FILTER_MODES.CUIJ) {
      return rule.filter.cuij || "CUIJ pendiente";
    }
    if (rule.filter.mode === FILTER_MODES.SAVED_EXPEDIENTES) {
      return rule.filter.savedExpedientes.mode === SAVED_EXPEDIENT_MODES.ALL
        ? "Todos mis expedientes"
        : `${rule.filter.savedExpedientes.cuijs.length} seleccionados`;
    }
    if (rule.filter.mode === FILTER_MODES.FOLLOW_UPS) {
      return rule.filter.followUps.mode === SAVED_EXPEDIENT_MODES.ALL
        ? "Todos mis seguimientos"
        : `${rule.filter.followUps.cuijs.length} seleccionados`;
    }
    if (rule.filter.mode === FILTER_MODES.INTEGRATION) {
      return "Integración · Notificaciones del PJM";
    }
    return `${rule.filter.jurisdictions.length} ${
      rule.filter.jurisdictions.length === 1
        ? "combinación"
        : "combinaciones"
    }`;
  }, [rule.filter]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setSaving(true);
    const filter = {
      ...rule.filter,
      cuij: rule.filter.mode === FILTER_MODES.CUIJ ? rule.filter.cuij : "",
      jurisdictions:
        rule.filter.mode === FILTER_MODES.JURISDICTION
          ? rule.filter.jurisdictions
          : [],
      savedExpedientes:
        rule.filter.mode === FILTER_MODES.SAVED_EXPEDIENTES
          ? rule.filter.savedExpedientes
          : { mode: SAVED_EXPEDIENT_MODES.ALL, cuijs: [] },
      followUps:
        rule.filter.mode === FILTER_MODES.FOLLOW_UPS
          ? rule.filter.followUps
          : { mode: SAVED_EXPEDIENT_MODES.ALL, cuijs: [] },
      integration:
        rule.filter.mode === FILTER_MODES.INTEGRATION
          ? { type: INTEGRATION_TYPES.PJM_NOTIFICATIONS }
          : undefined,
    };
    const payload = {
      ...rule,
      name: computedName,
      filter,
      event:
        rule.event.type === EVENT_TYPES.STUCK
          ? rule.event
          : { type: rule.event.type },
    };
    try {
      if (id) await api.updateRule(id, payload);
      else await api.createRule(payload);
      navigate("/alertas");
    } catch (requestError) {
      setError(requestError.message);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="page form-loading">Cargando configuración…</div>;
  }

  return (
    <form className="page rule-form-page" onSubmit={submit}>
      <div className="form-topbar">
        <button type="button" className="back-link" onClick={() => navigate("/alertas")}>
          <ArrowLeft size={18} /> Volver a alertas
        </button>
        <div className="form-top-actions">
          <button
            type="button"
            className="button secondary"
            onClick={() => navigate("/alertas")}
          >
            Cancelar
          </button>
          <button className="button primary" disabled={saving}>
            {saving ? "Guardando…" : id ? "Guardar cambios" : "Crear alerta"}
          </button>
        </div>
      </div>

      <div className="form-heading">
        <span className="eyebrow">{id ? "EDITAR ALERTA" : "NUEVA ALERTA"}</span>
        <h1>{id ? "Ajustá el seguimiento" : "¿Qué querés seguir?"}</h1>
        <p>Definí el alcance, el evento y quiénes deben recibir el aviso.</p>
      </div>

      {error && <div className="inline-error form-error">{error}</div>}

      <div className="form-layout">
        <div className="form-sections">
          <section className="form-section">
            <div className="form-section-number">1</div>
            <div className="form-section-content">
              <div className="section-title">
                <h2>Nombre y alcance</h2>
                <p>Usá un nombre fácil de reconocer para todo el equipo.</p>
              </div>
              <label className="field">
                <span>Nombre de la alerta</span>
                <input
                  value={rule.name}
                  onChange={(event) =>
                    setRule((current) => ({ ...current, name: event.target.value }))
                  }
                  placeholder={computedName || "Ej. Movimientos civiles · Primera"}
                  maxLength={100}
                />
              </label>

              <div className="option-cards scope-options" aria-label="Alcance">
                {[
                  {
                    value: FILTER_MODES.CUIJ,
                    title: "Por CUIJ",
                    description: "Un expediente judicial específico.",
                    icon: BriefcaseBusiness,
                  },
                  {
                    value: FILTER_MODES.JURISDICTION,
                    title: "Por organismo",
                    description: "Una o más combinaciones de organismos.",
                    icon: Scale,
                  },
                  {
                    value: FILTER_MODES.SAVED_EXPEDIENTES,
                    title: "Mis expedientes",
                    description: "Todos o algunos expedientes guardados.",
                    icon: Files,
                  },
                  {
                    value: FILTER_MODES.FOLLOW_UPS,
                    title: "Mis seguimientos",
                    description: "Todos o algunos expedientes en seguimiento.",
                    icon: CalendarClock,
                  },
                  ...(pjmIntegrationConnected
                    ? [{
                      value: FILTER_MODES.INTEGRATION,
                      title: "Integración: Notificaciones del PJM",
                      description:
                        "Novedades de la integración conectada.",
                      icon: Plug,
                    }]
                    : []),
                ].map(({ value, title, description, icon: Icon }) => (
                  <button
                    type="button"
                    key={value}
                    className={
                      rule.filter.mode === value
                        ? "option-card active"
                        : "option-card"
                    }
                    onClick={() =>
                      setRule((current) => ({
                        ...current,
                        filter: { ...current.filter, mode: value },
                        event: {
                          ...current.event,
                          type: value === FILTER_MODES.INTEGRATION
                            ? EVENT_TYPES.NEW_INTEGRATION_NOTIFICATION
                            : current.event.type ===
                                EVENT_TYPES.NEW_INTEGRATION_NOTIFICATION
                              ? EVENT_TYPES.ANY_MOVEMENT
                              : current.event.type,
                        },
                      }))
                    }
                  >
                    <span className="option-icon"><Icon size={18} /></span>
                    <span>
                      <strong>{title}</strong>
                      <small>{description}</small>
                    </span>
                    <span className="radio-indicator">
                      {rule.filter.mode === value && <span />}
                    </span>
                  </button>
                ))}
              </div>

              {rule.filter.mode === FILTER_MODES.CUIJ ? (
                <label className="field">
                  <span>CUIJ del expediente</span>
                  <input
                    className="cuij-input"
                    value={rule.filter.cuij}
                    onChange={(event) =>
                      setRule((current) => ({
                        ...current,
                        filter: {
                          ...current.filter,
                          cuij: formatCuij(event.target.value),
                        },
                      }))
                    }
                    placeholder="13-07195681-9/1"
                    inputMode="numeric"
                    required
                  />
                  <small>Formato: 13-07195681-9 o 13-07195681-9/1</small>
                </label>
              ) : rule.filter.mode === FILTER_MODES.JURISDICTION ? (
                <div className="jurisdiction-builder">
                  {optionsLoading && (
                    <div
                      className="options-loading"
                      role="status"
                      aria-live="polite"
                    >
                      <LoaderCircle size={16} />
                      <span>Consultando organismos…</span>
                    </div>
                  )}
                  <div className="select-grid">
                    <label className="field">
                      <span>Circunscripción</span>
                      <select
                        value={selection.circunscripcion}
                        disabled={optionsLoading}
                        onChange={(event) =>
                          setSelection({
                            circunscripcion: event.target.value,
                            type: "",
                            court: "",
                          })
                        }
                      >
                        <option value="">
                          {optionsLoading ? "Cargando…" : "Seleccionar"}
                        </option>
                        {circunscripciones.map((item) => (
                          <option key={item.id} value={item.id}>
                            {titleCase(item.label)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span>Tipo</span>
                      <select
                        value={selection.type}
                        disabled={!selection.circunscripcion || optionsLoading}
                        onChange={(event) =>
                          setSelection((current) => ({
                            ...current,
                            type: event.target.value,
                            court: "",
                          }))
                        }
                      >
                        <option value="">Seleccionar</option>
                        {types.map((item) => (
                          <option key={item.id} value={item.id}>
                            {titleCase(item.label)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span>Juzgado</span>
                      <select
                        value={selection.court}
                        disabled={!selection.type || optionsLoading}
                        onChange={(event) =>
                          setSelection((current) => ({
                            ...current,
                            court: event.target.value,
                          }))
                        }
                      >
                        <option value="">Seleccionar</option>
                        {courts.map((item) => (
                          <option key={item.id} value={item.id}>
                            {titleCase(item.label)}
                            {item.id === ALL_COURTS_ID ? " — incluye actuales y futuros" : ""}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <button
                    type="button"
                    className="button secondary add-combination"
                    onClick={addJurisdiction}
                    disabled={!selection.court}
                  >
                    <Plus size={17} /> Agregar combinación
                  </button>

                  {rule.filter.jurisdictions.length > 0 && (
                    <div className="combination-list">
                      <span className="combination-caption">
                        Coincide con cualquiera de estas combinaciones
                      </span>
                      {rule.filter.jurisdictions.map((item, index) => (
                        <div
                          className="combination-item"
                          key={`${item.circunscripcionId}-${item.typeId}-${item.courtId}`}
                        >
                          <span className="combination-or">
                            {index === 0 ? <Check size={15} /> : "O"}
                          </span>
                          <span>
                            <strong>{item.circunscripcionLabel}</strong>
                            <small>
                              {item.typeLabel} · {item.courtLabel}
                            </small>
                          </span>
                          <button
                            type="button"
                            className="icon-button"
                            onClick={() => removeJurisdiction(index)}
                            aria-label="Quitar combinación"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : rule.filter.mode === FILTER_MODES.INTEGRATION ? (
                <div className="integration-scope-note">
                  <Plug size={18} />
                  <span>
                    Esta alerta se aplicará a las nuevas notificaciones que
                    Praxis encuentre al sincronizar el PJM.
                  </span>
                </div>
              ) : (
                <div className="saved-expediente-builder">
                  <div className="segmented-control saved-expediente-mode">
                    <button
                      type="button"
                      className={
                        activeCollection.mode ===
                        SAVED_EXPEDIENT_MODES.ALL
                          ? "active"
                          : ""
                      }
                      onClick={() =>
                        setRule((current) => ({
                          ...current,
                          filter: {
                            ...current.filter,
                            [activeCollectionKey]: {
                              ...current.filter[activeCollectionKey],
                              mode: SAVED_EXPEDIENT_MODES.ALL,
                            },
                          },
                        }))
                      }
                    >
                      Todos
                    </button>
                    <button
                      type="button"
                      className={
                        activeCollection.mode ===
                        SAVED_EXPEDIENT_MODES.SELECTED
                          ? "active"
                          : ""
                      }
                      onClick={() =>
                        setRule((current) => ({
                          ...current,
                          filter: {
                            ...current.filter,
                            [activeCollectionKey]: {
                              ...current.filter[activeCollectionKey],
                              mode: SAVED_EXPEDIENT_MODES.SELECTED,
                            },
                          },
                        }))
                      }
                    >
                      Seleccionar expedientes
                    </button>
                  </div>

                  {activeCollection.mode ===
                  SAVED_EXPEDIENT_MODES.ALL ? (
                    <div className="saved-expediente-all-note">
                      <Check size={16} />
                      <span>
                        Incluye todos los expedientes en {activeCollectionLabel}
                        {" "}y también los que agregues allí en el futuro.
                      </span>
                    </div>
                  ) : (
                    <>
                      <label className="search-field saved-expedientes-search">
                        <Search size={16} />
                        <input
                          value={savedExpedienteSearch}
                          onChange={(event) =>
                            setSavedExpedienteSearch(event.target.value)
                          }
                          placeholder={`Buscar en ${activeCollectionLabel}`}
                          aria-label={`Buscar en ${activeCollectionLabel}`}
                        />
                      </label>

                      {activeExpedientesLoading ? (
                        <div className="options-loading saved-expedientes-loading">
                          <LoaderCircle size={16} />
                          <span>Cargando {activeCollectionLabel}…</span>
                        </div>
                      ) : activeExpedienteChoices.length ? (
                        <div className="saved-expediente-picker">
                          {visibleActiveExpedientes.map((expediente) => {
                            const selected =
                              activeCollection.cuijs.some(
                                (cuij) =>
                                  normalizeCuij(cuij) ===
                                  normalizeCuij(expediente.cuij),
                              );
                            return (
                              <button
                                type="button"
                                key={expediente.cuij}
                                className={
                                  selected
                                    ? "saved-expediente-option active"
                                    : "saved-expediente-option"
                                }
                                onClick={() =>
                                  toggleScopeExpediente(expediente.cuij)
                                }
                                aria-pressed={selected}
                              >
                                <span className="expediente-file-icon">
                                  {usesFollowUps ? (
                                    <CalendarClock size={17} />
                                  ) : (
                                    <Files size={17} />
                                  )}
                                </span>
                                <span>
                                  <strong>{expediente.cuij}</strong>
                                  <small>
                                    {expediente.caratula ||
                                      "Sin carátula informada"}
                                  </small>
                                </span>
                                <span className="checkbox-indicator">
                                  {selected && <Check size={13} />}
                                </span>
                              </button>
                            );
                          })}
                          {!visibleActiveExpedientes.length && (
                            <p className="saved-expedientes-empty">
                              No encontramos expedientes con esa búsqueda.
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="saved-expedientes-empty">
                          <p>
                            Todavía no agregaste expedientes a{" "}
                            {activeCollectionLabel}.
                          </p>
                          <button
                            type="button"
                            className="button secondary"
                            onClick={() =>
                              navigate(
                                usesFollowUps
                                  ? "/seguimientos"
                                  : "/expedientes",
                              )
                            }
                          >
                            Ir a {activeCollectionLabel}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="form-section">
            <div className="form-section-number">2</div>
            <div className="form-section-content">
              <div className="section-title">
                <h2>Evento</h2>
                <p>Cada alerta monitorea un solo tipo de evento.</p>
              </div>
              <div className="option-cards event-options">
                {(rule.filter.mode === FILTER_MODES.INTEGRATION
                  ? [integrationEventOption]
                  : eventOptions
                ).map(({ value, title, description, icon: Icon }) => (
                  <button
                    type="button"
                    key={value}
                    className={rule.event.type === value ? "option-card active" : "option-card"}
                    onClick={() =>
                      setRule((current) => ({
                        ...current,
                        event: { ...current.event, type: value },
                      }))
                    }
                  >
                    <span className="option-icon">
                      <Icon size={19} />
                    </span>
                    <span>
                      <strong>{title}</strong>
                      <small>{description}</small>
                    </span>
                    <span className="radio-indicator">
                      {rule.event.type === value && <span />}
                    </span>
                  </button>
                ))}
              </div>
              {rule.event.type === EVENT_TYPES.STUCK && (
                <label className="field inline-number-field">
                  <span>Avisar después de</span>
                  <span className="number-input-wrap">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={rule.event.stuckBusinessDays}
                      onChange={(event) =>
                        setRule((current) => ({
                          ...current,
                          event: {
                            ...current.event,
                            stuckBusinessDays: Number(event.target.value),
                          },
                        }))
                      }
                    />
                    días hábiles sin movimiento
                  </span>
                </label>
              )}
            </div>
          </section>

          <section className="form-section">
            <div className="form-section-number">3</div>
            <div className="form-section-content">
              <div className="section-title">
                <h2>Canales</h2>
                <p>Podés elegir más de uno para la misma alerta.</p>
              </div>
              <div className="option-cards channel-options">
                {channelOptions.map(({ value, label, icon: Icon }) => (
                  <button
                    type="button"
                    key={value}
                    className={rule.channels.includes(value) ? "option-card active" : "option-card"}
                    onClick={() => toggleChannel(value)}
                  >
                    <span className="option-icon">
                      <Icon size={19} />
                    </span>
                    <span>
                      <strong>{label}</strong>
                      <small>Usa los datos guardados en cada perfil.</small>
                    </span>
                    <span className="checkbox-indicator">
                      {rule.channels.includes(value) && <Check size={13} />}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section className="form-section">
            <div className="form-section-number">4</div>
            <div className="form-section-content">
              <div className="section-title">
                <h2>Destinatarios</h2>
                <p>Elegí quiénes del equipo deben recibir este aviso.</p>
              </div>
              <div className="segmented-control recipient-mode">
                <button
                  type="button"
                  className={rule.recipients.mode === "all" ? "active" : ""}
                  onClick={() =>
                    setRule((current) => ({
                      ...current,
                      recipients: { ...current.recipients, mode: "all" },
                    }))
                  }
                >
                  <Users size={17} /> Todo el equipo
                </button>
                <button
                  type="button"
                  className={rule.recipients.mode === "selected" ? "active" : ""}
                  onClick={() =>
                    setRule((current) => ({
                      ...current,
                      recipients: { ...current.recipients, mode: "selected" },
                    }))
                  }
                >
                  <Check size={17} /> Personas específicas
                </button>
              </div>
              {rule.recipients.mode === "selected" && (
                <div className="team-picker">
                  {team.map((member) => (
                    <button
                      type="button"
                      key={member.id}
                      className={
                        rule.recipients.userIds.includes(member.id)
                          ? "team-option active"
                          : "team-option"
                      }
                      onClick={() => toggleRecipient(member.id)}
                    >
                      <span className="member-avatar">{initials(member.name)}</span>
                      <span>
                        <strong>{member.name}</strong>
                        <small>{member.email}</small>
                      </span>
                      <span className="checkbox-indicator">
                        {rule.recipients.userIds.includes(member.id) && (
                          <Check size={13} />
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>

        <aside className="rule-summary">
          <span className="summary-kicker">RESUMEN</span>
          <h2>{computedName || "Tu nueva alerta"}</h2>
          <div className="summary-block">
            <span>Alcance</span>
            <strong>{scopeSummary}</strong>
          </div>
          <div className="summary-block">
            <span>Evento</span>
            <strong>{EVENT_LABELS[rule.event.type]}</strong>
          </div>
          <div className="summary-block">
            <span>Canales</span>
            <div className="summary-tags">
              {rule.channels.map((channel) => (
                <em key={channel}>{CHANNEL_LABELS[channel]}</em>
              ))}
            </div>
          </div>
          <div className="summary-block">
            <span>Destinatarios</span>
            <strong>
              {rule.recipients.mode === "all"
                ? "Todo el equipo"
                : `${rule.recipients.userIds.length} seleccionados`}
            </strong>
          </div>
          <div className="summary-note">
            <Check size={16} />
            Se enviará una sola notificación agrupada por evento.
          </div>
        </aside>
      </div>
    </form>
  );
}

function titleCase(value = "") {
  return value
    .toLocaleLowerCase("es-AR")
    .replace(/(^|\s)\p{L}/gu, (letter) => letter.toLocaleUpperCase("es-AR"));
}

function initials(name = "") {
  return name
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}
